/**
 * LegalVersionsAdmin — versiones de condiciones y licencia (MVP-02B §5).
 *
 * Hace operable desde la administración lo que antes exigía Django Admin o
 * `manage.py`. Registra **referencias** de versión, nunca el texto legal: los
 * documentos reales siguen pendientes de D-05/MVP-08 y esta pantalla no los
 * inventa ni los sustituye.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    AlertTriangle, CheckCircle2, ExternalLink, FlaskConical, Plus, RotateCw, Scale,
} from "lucide-react";

import { Checkbox, TextInput } from "../../../../components/ui/index.js";

const VERSIONS_URL = "/legal/versions/";

const FORM_VACIO = {
    kind: "general_terms",
    version_label: "",
    reference_url: "",
    is_test_fixture: false,
};

const formatFecha = (valor) =>
    valor ? new Date(valor).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "—";

const errorDe = (err, fallback) =>
    err?.response?.data?.detail
    || (typeof err?.response?.data === "string" ? err.response.data : null)
    || fallback;

export default function LegalVersionsAdmin() {
    const [recurso, setRecurso] = useState({ state: "loading", data: null, error: null });
    const [form, setForm] = useState(FORM_VACIO);
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [activating, setActivating] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [aviso, setAviso] = useState(null);

    const cargar = useCallback(() => {
        setRecurso((prev) => ({ ...prev, state: "loading", error: null }));
        return axios.get(VERSIONS_URL)
            .then(({ data }) => setRecurso({ state: "ready", data, error: null }))
            .catch((err) => setRecurso({
                state: "error",
                data: null,
                error: errorDe(err, "No se pudieron cargar las versiones legales."),
            }));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const crear = async (event) => {
        event.preventDefault();
        setFormErrors({});
        setAviso(null);
        setSaving(true);
        try {
            await axios.post(VERSIONS_URL, form);
            setForm(FORM_VACIO);
            setShowForm(false);
            await cargar();
        } catch (err) {
            const data = err?.response?.data;
            setFormErrors(
                data && typeof data === "object" && !Array.isArray(data)
                    ? Object.fromEntries(
                        Object.entries(data).map(([k, v]) => [k, Array.isArray(v) ? v.join(" ") : String(v)]),
                    )
                    : { general: errorDe(err, "No se pudo crear la versión.") },
            );
        } finally {
            setSaving(false);
        }
    };

    const activar = async (version) => {
        setAviso(null);
        setActivating(version.id);
        try {
            await axios.post(`${VERSIONS_URL}${version.id}/activate/`);
            await cargar();
        } catch (err) {
            setAviso({ tipo: "error", texto: errorDe(err, "No se pudo activar la versión.") });
        } finally {
            setActivating(null);
        }
    };

    const versiones = recurso.data?.versions ?? [];
    const fixturesPermitidos = Boolean(recurso.data?.test_fixtures_allowed);
    const kinds = recurso.data?.kinds ?? [];

    const activaDe = (kind) => versiones.find((v) => v.kind === kind && v.is_active) ?? null;

    return (
        <div className="p-4 lg:p-6 flex flex-col gap-4 h-full overflow-auto">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Scale className="w-7 h-7 text-primary shrink-0" />
                    <div>
                        <h1 className="text-xl font-bold">Versiones legales</h1>
                        <p className="text-sm text-base-content/60">
                            Referencias de versión de condiciones y licencia. El texto legal vive en el
                            documento enlazado, no aquí.
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={cargar} className="btn btn-sm btn-ghost gap-1">
                        <RotateCw className="w-4 h-4" /> Actualizar
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowForm((v) => !v)}
                        className="btn btn-sm btn-primary gap-1"
                    >
                        <Plus className="w-4 h-4" /> Nueva versión
                    </button>
                </div>
            </header>

            {/* Estado de lo que el checkout necesita */}
            {recurso.state === "ready" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {kinds.map(({ value, label }) => {
                        const activa = activaDe(value);
                        return (
                            <div
                                key={value}
                                className={`rounded-xl border p-3 ${activa ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"}`}
                            >
                                <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                                    {label}
                                </p>
                                {activa ? (
                                    <>
                                        <p className="font-semibold mt-1 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                                            {activa.version_label}
                                            {activa.is_test_fixture && (
                                                <span className="badge badge-warning badge-sm gap-1">
                                                    <FlaskConical className="w-3 h-3" /> QA
                                                </span>
                                            )}
                                        </p>
                                        {activa.reference_url && (
                                            <a
                                                href={activa.reference_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="link link-primary text-xs inline-flex items-center gap-1 mt-1"
                                            >
                                                Ver documento <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </>
                                ) : (
                                    <p className="mt-1 text-sm flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                                        Sin versión activa
                                        <span className="text-base-content/60">
                                            {value === "general_terms"
                                                ? "· bloquea todo el checkout"
                                                : "· bloquea el checkout digital"}
                                        </span>
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!fixturesPermitidos && recurso.state === "ready" && (
                <p className="text-xs text-base-content/50">
                    Este entorno no permite activar versiones marcadas como fixture de QA. Para
                    habilitarlas en local o preproducción, define
                    <code className="mx-1">ALLOW_TEST_FIXTURE_LEGAL_VERSIONS=True</code>.
                    Producción nunca debe activarlo.
                </p>
            )}

            {aviso && (
                <div role="alert" className="alert alert-error text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    {aviso.texto}
                </div>
            )}

            {/* Alta de versión */}
            {showForm && (
                <form onSubmit={crear} className="glassCard rounded-xl p-4 flex flex-col gap-3">
                    <h2 className="font-semibold">Registrar una referencia de versión</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="form-control gap-1">
                            <label className="label py-0" htmlFor="legal-kind">
                                <span className="label-text text-sm font-semibold">Documento *</span>
                            </label>
                            <select
                                id="legal-kind"
                                className="select select-bordered"
                                value={form.kind}
                                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                            >
                                {kinds.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control gap-1">
                            <label className="label py-0" htmlFor="legal-label">
                                <span className="label-text text-sm font-semibold">Etiqueta de versión *</span>
                            </label>
                            <TextInput
                                id="legal-label"
                                value={form.version_label}
                                onChange={(e) => setForm((f) => ({ ...f, version_label: e.target.value }))}
                                variant={formErrors.version_label ? "error" : undefined}
                                placeholder="condiciones-2026-01"
                            />
                            {formErrors.version_label && (
                                <span role="alert" className="label-text-alt text-error">{formErrors.version_label}</span>
                            )}
                        </div>
                        <div className="form-control gap-1">
                            <label className="label py-0" htmlFor="legal-url">
                                <span className="label-text text-sm font-semibold">URL del documento</span>
                            </label>
                            <TextInput
                                id="legal-url"
                                type="url"
                                value={form.reference_url}
                                onChange={(e) => setForm((f) => ({ ...f, reference_url: e.target.value }))}
                                variant={formErrors.reference_url ? "error" : undefined}
                                placeholder="https://…"
                            />
                            {formErrors.reference_url && (
                                <span role="alert" className="label-text-alt text-error">{formErrors.reference_url}</span>
                            )}
                        </div>
                    </div>

                    <Checkbox
                        checked={form.is_test_fixture}
                        onChange={(v) => setForm((f) => ({ ...f, is_test_fixture: v }))}
                        label="Es un fixture de QA (nunca un documento legal real)"
                    />

                    {formErrors.general && (
                        <p role="alert" className="text-sm text-error">{formErrors.general}</p>
                    )}

                    <div className="flex gap-2">
                        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                            {saving ? "Guardando…" : "Registrar versión"}
                        </button>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowForm(false)}>
                            Cancelar
                        </button>
                    </div>
                </form>
            )}

            {/* Listado */}
            {recurso.state === "loading" && (
                <div className="flex flex-col gap-2" aria-busy="true">
                    <div className="h-10 rounded-lg bg-base-300/60 animate-pulse" />
                    <div className="h-10 rounded-lg bg-base-300/60 animate-pulse" />
                    <span className="sr-only">Cargando versiones legales…</span>
                </div>
            )}

            {recurso.state === "error" && (
                <div role="alert" className="alert alert-error text-sm flex-wrap">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{recurso.error}</span>
                    <button type="button" onClick={cargar} className="btn btn-xs gap-1">
                        <RotateCw className="w-3 h-3" /> Reintentar
                    </button>
                </div>
            )}

            {recurso.state === "ready" && versiones.length === 0 && (
                <div className="rounded-xl border border-base-300 p-6 text-center">
                    <Scale className="w-8 h-8 mx-auto text-base-content/30 mb-2" />
                    <p className="font-semibold">Todavía no hay ninguna versión registrada</p>
                    <p className="text-sm text-base-content/60">
                        Sin condiciones generales activas, el checkout está bloqueado.
                    </p>
                </div>
            )}

            {recurso.state === "ready" && versiones.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-base-300">
                    <table className="table table-sm table-zebra">
                        <thead>
                            <tr>
                                <th>Documento</th>
                                <th>Versión</th>
                                <th>Estado</th>
                                <th>Creada por</th>
                                <th>Fechas</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {versiones.map((version) => (
                                <tr key={version.id}>
                                    <td className="whitespace-nowrap">{version.kind_display}</td>
                                    <td>
                                        <span className="font-medium">{version.version_label}</span>
                                        {version.reference_url ? (
                                            <a
                                                href={version.reference_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="link link-primary text-xs ml-2 inline-flex items-center gap-1"
                                            >
                                                documento <ExternalLink className="w-3 h-3" />
                                            </a>
                                        ) : (
                                            <span className="text-xs text-base-content/40 ml-2">sin URL</span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap">
                                        {version.is_active ? (
                                            <span className="badge badge-success badge-sm">Activa</span>
                                        ) : (
                                            <span className="badge badge-ghost badge-sm">Histórica</span>
                                        )}
                                        {version.is_test_fixture && (
                                            <span className="badge badge-warning badge-sm ml-1 gap-1">
                                                <FlaskConical className="w-3 h-3" /> QA
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-xs text-base-content/60">{version.created_by ?? "—"}</td>
                                    <td className="text-xs text-base-content/60 whitespace-nowrap">
                                        <div>Creada: {formatFecha(version.created_at)}</div>
                                        <div>Activada: {formatFecha(version.activated_at)}</div>
                                    </td>
                                    <td className="text-right">
                                        {!version.is_active && (
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-outline"
                                                disabled={
                                                    activating === version.id
                                                    || (version.is_test_fixture && !fixturesPermitidos)
                                                    || (!version.is_test_fixture && !version.reference_url)
                                                }
                                                title={
                                                    version.is_test_fixture && !fixturesPermitidos
                                                        ? "Este entorno no permite activar fixtures de QA."
                                                        : !version.is_test_fixture && !version.reference_url
                                                          ? "Una versión real necesita la URL del documento."
                                                          : undefined
                                                }
                                                onClick={() => activar(version)}
                                            >
                                                {activating === version.id ? "Activando…" : "Activar"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-xs text-base-content/40">
                Las aceptaciones de cliente son de solo lectura y no se pueden editar ni borrar:
                cada pedido conserva la versión exacta que se aceptó.
            </p>
        </div>
    );
}
